import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';
import DatePicker from 'components/DatePicker/DatePicker';
import NumberField from 'components/NumberField/NumberField';
import Select from 'components/Select/Select';
import { DATA_NEW_PROFIT_SUPPLIERS, UID_NAME_TUPLES } from 'constants/dataSets';
import {
  PRODUCT_TYPE, PROFIT_CENTER_LINKS,
} from 'constants/formFields';
import { DATE_FORMAT } from 'constants/general';
import { NUMBER_FORMATS, VALUE_CHAIN_UNIT_TYPES } from 'constants/interfaces';
import ProductUnitLinksField from 'pages/UserPages/CreateProduct/components/ProductUnitLinksField';
import { useUserMeQuery } from 'store/slices/user/apis/user';
import { getRelationTypeByProductType } from 'utils/formHelpers/productTypeValueChain';

import type { TDate } from 'components/DatePicker/interfaces/TDate';
import type { FieldPath } from 'react-hook-form';
import type IProfitCenterLink from 'store/slices/dataSets/interfaces/IProfitCenterLink';
import type { TCreateProductSchema } from 'utils/validators';
import type INumberFieldValue from './interfaces/INumberFieldValue';
import type IProfitCenterLinksProps from './interfaces/IProfitCenterLinksProps';
import type ISelectOption from './interfaces/ISelectOption';

import styles from './sass/ProfitCenterLinksProduct.module.scss';

function ProfitCenterLinksProduct({
  control,
  profitCenterIds,
  placeholder = '',
  watch,
  setValue,
  hideField,
  isManufacturedByVisible,
}: IProfitCenterLinksProps) {
  const { t } = useTranslation();
  const { recordId = '' } = useParams();
  const profitCenterLinks = watch(PROFIT_CENTER_LINKS) || [];
  // Output products are manufactured by balance sheet assets; input products are supplied
  // instead, so the per-profit-centre BSA links only apply to output products.
  const isOutputSourced = getRelationTypeByProductType(watch(PRODUCT_TYPE)?.value)
    === VALUE_CHAIN_UNIT_TYPES.BALANCE_SHEET_ASSET;
  const { data: userData } = useUserMeQuery();
  const companyCurrency = userData?.data?.company?.currency;
  const profitCenterOptions = profitCenterIds?.[UID_NAME_TUPLES.profitCenter] || [];
  const maxProfitCenters = profitCenterOptions.length;

  const handleAddProfitCenter = () => {
    if (profitCenterLinks.length >= maxProfitCenters) {
      return;
    }
    const newProfitCenter = {
      ...DATA_NEW_PROFIT_SUPPLIERS,
      percentOfTotalCost: 0,
      expectedGrowthDecline: 0,
    };

    setValue(PROFIT_CENTER_LINKS, [...profitCenterLinks, newProfitCenter]);
  };

  const handleRemoveProfitCenter = (index: number) => {
    const updatedLinks = [...profitCenterLinks];
    updatedLinks.splice(index, 1);
    setValue(PROFIT_CENTER_LINKS, updatedLinks);
  };

  const updateProfitCenterField = (index: number, field: keyof IProfitCenterLink, value: string | number | TDate) => {
    const updatedLinks = [...profitCenterLinks];
    updatedLinks[index] = {
      ...updatedLinks[index],
      [field]: value,
    };
    setValue(PROFIT_CENTER_LINKS, updatedLinks);
  };

  if (profitCenterLinks.length === 0 && maxProfitCenters > 0) {
    handleAddProfitCenter();
  }

  return (
    <div className={styles.container}>
      {profitCenterLinks.map((profitCenter: IProfitCenterLink, index: number) => {
        const profitCenterId = `profit-center-${profitCenter.profitCenterId || index}`;
        return (
          <div key={profitCenterId} className={styles.profitCenterItem}>
            <h3>
              {t('common.profitCentre', { number: index + 1 })}
            </h3>

            {profitCenterLinks.length > 1 && index !== 0 && (
              <button
                type="button"
                className={styles.deleteButton}
                onClick={() => handleRemoveProfitCenter(index)}
                aria-label={t('common.delete')}
              >
                <TrashIcon />
              </button>
            )}

            <div className={`${styles.formRow} ${styles.formRowHalf}`}>
              <Controller
                control={control}
                name={`${PROFIT_CENTER_LINKS}.${index}.profitCenterId` as FieldPath<TCreateProductSchema>}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <Select
                    label={t('products.fields.profitCentre')}
                    placeholder={recordId ? placeholder : t('products.fields.profitCentre')}
                    options={profitCenterIds?.[UID_NAME_TUPLES.profitCenter]}
                    value={value ? { value, label: value } : null}
                    onChange={(newValue: unknown) => {
                      const selectedOption = newValue as ISelectOption | null;
                      onChange(selectedOption?.value || '');
                      updateProfitCenterField(index, 'profitCenterId', selectedOption?.value || '');
                    }}
                    error={error?.message}
                    isSearchable
                    menuPosition="absolute"
                    required
                  />
                )}
              />
            </div>

            <div className={styles.formRow}>
              <Controller
                control={control}
                name={`${PROFIT_CENTER_LINKS}.${index}.startDate` as FieldPath<TCreateProductSchema>}
                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                  <DatePicker
                    name={name}
                    label={t('products.fields.startDate')}
                    error={error?.message}
                    onChange={(date: TDate) => {
                      onChange(date);
                      updateProfitCenterField(index, 'startDate', date);
                    }}
                    value={value?.toString()}
                    placeholder={placeholder || DATE_FORMAT}
                    required
                    maxDate={new Date()}
                  />
                )}
              />

              <Controller
                control={control}
                name={`${PROFIT_CENTER_LINKS}.${index}.endDate` as FieldPath<TCreateProductSchema>}
                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                  <DatePicker
                    name={name}
                    label={t('products.fields.endDate')}
                    error={error?.message}
                    onChange={(date: TDate) => {
                      onChange(date);
                      updateProfitCenterField(index, 'endDate', date);
                    }}
                    value={value?.toString()}
                    minDate={profitCenter.startDate ? new Date(profitCenter.startDate) : undefined}
                    placeholder={placeholder || DATE_FORMAT}
                    required
                  />
                )}
              />
            </div>

            <div className={styles.formRow}>
              <Controller
                control={control}
                name={`${PROFIT_CENTER_LINKS}.${index}.totalCost` as FieldPath<TCreateProductSchema>}
                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                  <div className={styles.totalCostContainer}>
                    <div className={styles.fieldWithCurrency}>
                      <NumberField
                        name={name}
                        label={t('products.fields.suppliersTier1')}
                        value={value?.toString()}
                        error={error?.message}
                        onChange={(values: INumberFieldValue) => {
                          onChange(values.value);
                          updateProfitCenterField(index, 'totalCost', values.value);
                        }}
                        placeholder={placeholder || t('products.fields.suppliersTier1')}
                        className={styles.valueInput}
                        allowNegative={false}
                        hideTextError
                        required
                      />
                      <span className={`${styles.currencyInput} ${error?.message ? styles.errorBorder : ''}`}>
                        M
                        {' '}
                        {companyCurrency}
                      </span>
                    </div>
                    {error?.message ? <span className={styles.errorMessage}>{error.message}</span> : null}
                  </div>
                )}
              />

              <Controller
                control={control}
                name={`${PROFIT_CENTER_LINKS}.${index}.percentOfTotalCost` as FieldPath<TCreateProductSchema>}
                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                  <NumberField
                    name={name}
                    label={t('products.fields.suppliersTier1InPercentage')}
                    value={value?.toString()}
                    error={error?.message}
                    onChange={(values: INumberFieldValue) => {
                      onChange(values.value);
                      updateProfitCenterField(index, 'percentOfTotalCost', values.value);
                    }}
                    placeholder={placeholder || t('products.fields.suppliersTier1InPercentage')}
                    type={NUMBER_FORMATS.percent}
                    allowNegative={false}
                    required
                  />
                )}
              />
            </div>
            {isManufacturedByVisible && isOutputSourced && profitCenter.profitCenterId ? (
              <div className={styles.formRow}>
                <ProductUnitLinksField
                  control={control}
                  relationType={VALUE_CHAIN_UNIT_TYPES.BALANCE_SHEET_ASSET}
                  profitCentreUID={profitCenter.profitCenterId}
                  label={t('products.fields.manufacturedBy')}
                />
              </div>
            ) : null}

            {!hideField ? (
              <div className={`${styles.formRow} ${styles.formRowHalf}`}>
                <Controller
                  control={control}
                  name={`${PROFIT_CENTER_LINKS}.${index}.expectedGrowthDecline` as FieldPath<TCreateProductSchema>}
                  render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                    <NumberField
                      name={name}
                      label={t('products.fields.expectedGrowthDecline')}
                      value={value?.toString()}
                      error={error?.message}
                      onChange={(values: INumberFieldValue) => {
                        onChange(values.value);
                        updateProfitCenterField(index, 'expectedGrowthDecline', values.value);
                      }}
                      placeholder={placeholder || t('products.fields.expectedGrowthDecline')}
                      type={NUMBER_FORMATS.percent}
                      allowNegative={false}
                      required
                    />
                  )}
                />
              </div>
            ) : null}
          </div>
        );
      })}

      {profitCenterLinks.length < maxProfitCenters && (
        <button
          type="button"
          className={styles.addButton}
          onClick={handleAddProfitCenter}
          aria-label={t('common.add')}
        >
          <PlusIcon />
          {t('common.addProfitCentre')}
        </button>
      )}
    </div>

  );
}

export default ProfitCenterLinksProduct;
